import { type FC, useMemo } from "react";

import { ControlledFormCheckbox, RadioGroup, RadioGroupItem } from "src/components";
import { useTranslation } from "src/translations";
import type { QuestionnaireAnswer, QuestionnaireQuestion } from "src/types";

function shuffled<T>(arr: T[]): T[] {
    return arr
        .map((value) => ({ value, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => value);
}

type QuestionInputProps = {
    question: QuestionnaireQuestion;
    value: string | string[] | undefined;
    onChange: (value: string | string[]) => void;
    showError: boolean;
};

export const QuestionInput: FC<QuestionInputProps> = ({ question, value, onChange, showError }) => {
    const { t } = useTranslation();
    const shuffledAnswers = useMemo(
        () => (question.randomizeAnswersOrder ? shuffled(question.answers) : question.answers),
        [question.answers, question.randomizeAnswersOrder],
    );

    const error =
        showError && question.type === "SINGLE_SELECT" && !value ? t("verification.INVESTOR_QUESTIONNAIRE.requiredAnswer") : undefined;

    if (question.type === "SINGLE_SELECT") {
        return (
            <RadioGroup
                label={question.question}
                value={typeof value === "string" ? value : undefined}
                onValueChange={(v) => onChange(v)}
                orientation="vertical"
                className="gap-3 pl-4"
                error={error}
            >
                {shuffledAnswers.map((answer: QuestionnaireAnswer) => (
                    <RadioGroupItem key={answer.answerId} value={answer.answerId}>
                        {answer.answer}
                    </RadioGroupItem>
                ))}
            </RadioGroup>
        );
    }

    const selectedValues = Array.isArray(value) ? value : [];

    return (
        <fieldset className="flex flex-col gap-y-3 p-0">
            <p className="text-xl font-semibold">{question.question}</p>
            <div className="flex flex-col gap-y-3 pl-4">
                {shuffledAnswers.map((answer: QuestionnaireAnswer) => (
                    <label key={answer.answerId} className="flex items-center gap-2">
                        <ControlledFormCheckbox
                            checked={selectedValues.includes(answer.answerId)}
                            onCheckedChange={() => {
                                const next = selectedValues.includes(answer.answerId)
                                    ? selectedValues.filter((id) => id !== answer.answerId)
                                    : [...selectedValues, answer.answerId];
                                onChange(next);
                            }}
                        />
                        <span className="peer-disabled:cursor-not-allowed">{answer.answer}</span>
                    </label>
                ))}
            </div>
        </fieldset>
    );
};
